import { parseAsArrayOf, parseAsString } from 'nuqs';

export type FilterFieldType = 'search' | 'multi';

export interface FilterField<K extends string> {
  key: K;
  type: FilterFieldType;
  graphKey?: string;
}

type FilterValue = string | string[] | null | undefined;

export function buildFilterParsers<K extends string>(
  fields: Array<FilterField<K>>
) {
  return fields.reduce<Record<K, unknown>>(
    (acc, field) => {
      acc[field.key] =
        field.type === 'search'
          ? parseAsString.withDefault('')
          : parseAsArrayOf(parseAsString).withDefault([]);
      return acc;
    },
    {} as Record<K, unknown>
  );
}

export function normalizeFilters<
  T extends Record<K, unknown>,
  K extends string,
>(raw: Partial<Record<K, FilterValue>>, fields: Array<FilterField<K>>): T {
  const normalized = {} as Record<string, unknown>;

  fields.forEach((field) => {
    const value = raw[field.key];
    if (field.type === 'search') {
      normalized[field.key] = (value as string) || '';
    } else {
      normalized[field.key] = (value as string[]) || [];
    }
  });

  return normalized as T;
}

export function buildGraphQLFilter<K extends string>(
  filters: Partial<Record<K, FilterValue>>,
  fields: Array<FilterField<K>>
) {
  const filter: Record<string, unknown> = {};

  fields.forEach((field) => {
    const value = filters[field.key];
    const graphKey = field.graphKey ?? field.key;

    if (field.type === 'search') {
      if (value) {
        filter[graphKey] = value;
      }
    } else {
      const arr = (value as string[]) || [];
      if (arr.length > 0) {
        filter[graphKey] = arr;
      }
    }
  });

  return Object.keys(filter).length > 0 ? filter : undefined;
}

export function hasActiveFilters<K extends string>(
  filters: Partial<Record<K, FilterValue>>,
  fields: Array<FilterField<K>>
) {
  return fields.some((field) => {
    const value = filters[field.key];
    return field.type === 'search'
      ? !!value
      : Array.isArray(value) && value.length > 0;
  });
}

export function clearPayload<K extends string>(fields: Array<FilterField<K>>) {
  return fields.reduce<Record<string, null>>(
    (acc, field) => {
      acc[field.key] = null;
      return acc;
    },
    {} as Record<string, null>
  );
}
